import React, { useState, useEffect } from 'react';
import contractService from '../../services/contractService';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatAmount, formatDate } from '../../utils/formatters';

const ContractPaymentsModal = ({ isOpen, onClose, contractNumber }) => {
  const [loading, setLoading] = useState(false);
  const [contractDetails, setContractDetails] = useState(null);
  const [error, setError] = useState(null);

  // 🆕 NOUVEAU: Fonction pour calculer l'état du contrat (identique à ContractDetails)
  const calculateStatus = (contract) => {
    if (!contract) return "État non défini";
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (contract.remark === "R" || contract.remark === "NR") {
      if (contract.closingDate && contract.creationDate) {
        const diffTime = Math.abs(
          new Date(contract.closingDate) - new Date(contract.creationDate)
        );
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return `Rachat sous : ${diffDays} jour(s)`;
      }
    }

    if (contract.limitDate) {
      const limitDate = new Date(contract.limitDate);
      limitDate.setHours(0, 0, 0, 0);
      const diffTime = limitDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 0) {
        return "Dépassement d'échéance";
      } else {
        return `Reste : ${diffDays} jour(s) avant terme`;
      }
    }

    return contract.status || "État non défini";
  };

  // Charger les détails du contrat
  useEffect(() => {
    if (isOpen && contractNumber) {
      setLoading(true);
      setError(null);
      
      const loadContractDetails = async () => {
        try {
          // Rechercher le contrat par son numéro
          const result = await contractService.getContracts({
            search: contractNumber,
            limit: 1
          });
          
          if (result.success && result.data && result.data.length > 0) {
            setContractDetails(result.data[0]);
          } else {
            setError("Contrat non trouvé");
          }
        } catch (error) {
          console.error("Erreur lors du chargement du contrat:", error);
          setError("Erreur lors du chargement du contrat");
        } finally {
          setLoading(false);
        }
      };

      loadContractDetails();
    }
  }, [isOpen, contractNumber]);

  // Fermer avec Échap
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div 
          className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* En-tête */}
          <div className="bg-blue-50 px-6 py-4 border-b border-blue-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-blue-900">
                💰 Détails des versements - {contractNumber}
              </h2>

                    <h2 className="text-lg font-semibold text-yellow-800 mt-1">
                      
                      {calculateStatus(contractDetails)}

                    </h2>

              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Contenu */}
          <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-6">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <LoadingSpinner message="Chargement des détails..." />
              </div>
            ) : error ? (
              <div className="text-center text-red-600 py-8">
                <div className="text-4xl mb-2">😟</div>
                <p>{error}</p>
              </div>
            ) : contractDetails ? (
              <div className="space-y-6">
                {/* 🆕 MODIFIÉ: Résumé financier avec 5 cartes incluant la Remise */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                    <div className="text-lg font-bold text-blue-600">
                      {formatAmount(contractDetails.expectedAmount || 0)}
                    </div>
                    <div className="text-sm text-blue-600">Montant Prévu</div>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <div className="text-lg font-bold text-green-600">
                      {formatAmount(contractDetails.receivedAmount || 0)}
                    </div>
                    <div className="text-sm text-green-600">Total Perçu</div>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
                    <div className="text-lg font-bold text-orange-600">
                      {formatAmount(contractDetails.remainingAmount || 0)}
                    </div>
                    <div className="text-sm text-orange-600">Reste à Percevoir</div>
                  </div>
                  {/* 🆕 NOUVEAU: Carte Remise avec statut du contrat */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                    <div className="text-lg font-bold text-yellow-600">
                      {formatAmount(contractDetails.discount || 0)}
                    </div>
                    <div className="text-sm text-yellow-600">Remise</div>
                     {/* <div className="text-xs text-yellow-500 mt-1 font-medium">
                      {calculateStatus(contractDetails)}
                    </div>*/}
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                    <div className="text-lg font-bold text-purple-600">
                      {contractDetails.versements ? contractDetails.versements.length : 0}
                    </div>
                    <div className="text-sm text-purple-600">Nb Versements</div>
                  </div>
                </div>

                {/* Informations générales */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3">Informations générales</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div><span className="font-medium">Agence:</span> {contractDetails.agency}</div>
                    <div><span className="font-medium">Client:</span> {contractDetails.client || 'N/A'}</div>
                    <div><span className="font-medium">Date création:</span> {formatDate(contractDetails.creationDate)}</div>
                    <div><span className="font-medium">Date limite:</span> {formatDate(contractDetails.limitDate)}</div>
                    <div><span className="font-medium">Poids bijou:</span> {contractDetails.jewelWeight || 0} g</div>
                    <div><span className="font-medium">Montant décaissé:</span> {formatAmount(contractDetails.disbursementAmount || 0)}</div>
                  </div>
                </div>

                {/* Liste des versements */}
                <div className="bg-white border border-gray-200 rounded-lg">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <h3 className="text-lg font-semibold">
                      📋 Détail des versements
                      {contractDetails.versements && contractDetails.versements.length > 0 && (
                        <span className="text-sm font-normal text-gray-600 ml-2">
                          ({contractDetails.versements.length} versement{contractDetails.versements.length > 1 ? 's' : ''})
                        </span>
                      )}
                    </h3>
                    
                  </div>
                  
                  {contractDetails.versements && contractDetails.versements.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full table-auto">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">N°</th>
                            <th className="px-4 py-2 text-center text-sm font-medium text-gray-700">Date</th>
                            <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">Montant</th>
                            <th className="px-4 py-2 text-center text-sm font-medium text-gray-700">Type VP</th>
                            <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">Dégagement</th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">N° Reçu</th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Remarque</th>
                          </tr>
                        </thead>
                        <tbody>
                          {contractDetails.versements
                            .sort((a, b) => (a.ordreVers || 0) - (b.ordreVers || 0))
                            .map((versement, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}>
                              <td className="px-4 py-2 text-sm font-medium">{versement.ordreVers || '-'}</td>
                              <td className="px-4 py-2 text-sm text-center">
                                {versement.dateVerse ? formatDate(versement.dateVerse) : '-'}
                              </td>
                              <td className="px-4 py-2 text-sm text-right font-medium text-green-600">
                                {formatAmount(versement.montantPercu || 0)}
                              </td>
                              <td className="px-4 py-2 text-sm text-center">
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                  {versement.typeVP || '-'}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm text-right">
                                {versement.degagement ? formatAmount(versement.degagement) : '-'}
                              </td>
                              <td className="px-4 py-2 text-sm">{versement.numeroRecu || '-'}</td>
                              <td className="px-4 py-2 text-sm">{versement.remarqueVersement || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      
                      {/* Total des versements */}
                      <div className="border-t border-gray-200 bg-blue-50 px-4 py-3">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-blue-800">Total des versements:</span>
                          <span className="text-lg font-bold text-blue-600">
                            {formatAmount(
                              contractDetails.versements.reduce((sum, v) => sum + (parseFloat(v.montantPercu) || 0), 0)
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      <div className="text-4xl mb-2">📭</div>
                      <p>Aucun versement enregistré pour ce contrat</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <div className="text-4xl mb-2">📄</div>
                <p>Aucune donnée disponible</p>
              </div>
            )}
          </div>

          {/* Pied de page */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractPaymentsModal;